import { EntityInfoUtils } from '@record-it-npm/synchro-common';
import {
  Disposable,
  DisposableContainer
} from 'record-it-utils/utils/DisposableContainer/DisposableContainer';
import { RateLimit } from 'record-it-utils/utils/RateLimit/RateLimit';
import { ObjectUtils } from 'record-it-utils/utils/ObjectUtils/ObjectUtils';
import {
  RepositoryByEntityName,
  EntityRepositoryContainer
} from '../EntityRepositoryContainer/EntityRepositoryContainer';
import { HooksContainer } from '../HooksContainer/HooksContainer';
import { StorageAdapter } from '../StorageAdapter/StorageAdapter';
import {
  EntitySynchronizationInfo,
  EntitySynchronizationQueue,
  EntityToSynchronizeInfo,
  ModifyFunction,
  QueueItemType
} from './EntitySynchronizationQueue';
import { EntityManagerEntityTypesByEntityName } from '../EntityManagerEntityTypesByEntityName';
import { EntityWithEntityName } from '../EntityWithEntityName';
import { AnyEntityWithEntityName } from '../AnyEntityWithEntityName';
import { AnySynchronizationEnvironmentTypesConstraint } from '../SynchronizationEnvironmentTypes';

export class EntitySynchronization<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>,
  TRepositoryByEntityName extends RepositoryByEntityName<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName
  >
> {
  private static initializedInstance: EntitySynchronization<
    any,
    any,
    any
  > | null = null;
  private static readonly QUEUE_KEY = 'EntitySynchronization::queue';

  private initialized: boolean = false;
  private queue: EntitySynchronizationQueue<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName,
    AfterItemUploadedReturnValue<
      TSynchronizationEnvironmentTypes,
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
      TEntityTypesByEntityName
    >
  >;
  private hooksContainer: HooksContainer<
    EntitySynchronizationHooks<
      TSynchronizationEnvironmentTypes,
      TEntityTypesByEntityName
    >
  > = new HooksContainer();
  private disposableContainer: DisposableContainer = new DisposableContainer();

  private saveQueueRateLimited = RateLimit.rateLimitFunction(
    this.saveQueue.bind(this),
    50
  );

  constructor(
    private readonly entityRepositoryContainer: EntityRepositoryContainer<
      TSynchronizationEnvironmentTypes,
      TEntityTypesByEntityName,
      TRepositoryByEntityName
    >,
    uploadFunction: UploadFunction<
      TSynchronizationEnvironmentTypes,
      TEntityTypesByEntityName
    >
  ) {
    this.queue = new EntitySynchronizationQueue(
      entityRepositoryContainer.getAllEntityNames(),
      async (item) => {
        return {
          preSynchronizationEntity: JSON.parse(JSON.stringify(item.entity)),
          result: await uploadFunction(item)
        };
      },
      (uploadFunctionReturnValue) => {
        this.handleAfterItemUploaded(uploadFunctionReturnValue);
      },
      () => {
        this.hooksContainer.callHooks(
          'itemsToSynchronizeInfosChanged',
          this.getItemsToSynchronizeInfos()
        );
      }
    );
  }

  public async init(): Promise<void> {
    if (this.initialized) {
      return;
    }

    if (EntitySynchronization.initializedInstance) {
      // because they would generate the same ids since they are not synchronized
      throw new Error(
        'only one entitySynchronization is allowed to be initialized'
      );
    }
    EntitySynchronization.initializedInstance = this;

    await this.loadQueue();
    this.registerEntityHooks();

    this.initialized = true;
  }

  public async flush(): Promise<void> {
    if (this.saveQueueRateLimited.isPending()) {
      this.saveQueueRateLimited.cancel();
      await this.saveQueue();
    }
  }

  public destroy(): void {
    if (this.saveQueueRateLimited.isPending()) {
      throw new Error(
        "can't destroy the EntitySynchronization because a save is still pending"
      );
    }

    this.initialized = false;
    EntitySynchronization.initializedInstance = null;

    this.disposableContainer.disposeAll();
  }

  public start(): void {
    this.queue.start();
  }

  public stop(): void {
    this.queue.stop();
  }

  public bindItemsToSynchronizeInfos(
    callback: (
      itemsToSynchronizeInfos: Array<
        EntityToSynchronizeInfo<TSynchronizationEnvironmentTypes>
      >
    ) => void
  ): Disposable {
    callback(this.getItemsToSynchronizeInfos());
    return this.registerHooks({
      itemsToSynchronizeInfosChanged: callback
    });
  }

  public bindIsSynchronizing(
    callback: (isSynchronizing: boolean) => void
  ): Disposable {
    return this.queue.bindIsDraining(callback);
  }

  public getItemsToSynchronizeInfos(): Array<
    EntityToSynchronizeInfo<TSynchronizationEnvironmentTypes>
  > {
    this.assertInitialized();

    return this.queue.getItemsToSynchronizeInfos();
  }

  public registerEntitySpecificEntityIdUpgradedHook<
    TEntityName extends
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']
  >(
    entityName: TEntityName,
    callback: (entity: TEntityTypesByEntityName[TEntityName]['entity']) => void
  ): Disposable {
    return this.registerHooks({
      entityIdUpgraded: (entityWithEntityName) => {
        if (entityWithEntityName.entityName === entityName) {
          callback(entityWithEntityName.entity);
        }
      }
    });
  }

  public registerHooks(
    hooks: EntitySynchronizationHooks<
      TSynchronizationEnvironmentTypes,
      TEntityTypesByEntityName
    >
  ): Disposable {
    return this.hooksContainer.registerHooks(hooks);
  }

  public async waitUntilAllItemsAreSynchronized(): Promise<void> {
    await this.queue.waitUntilAllItemsAreSynchronized();
  }

  public entityIsInTheQueue(
    entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
    entityId: string
  ): boolean {
    return this.queue.entityIsIncluded(entityName, entityId);
  }

  public lock(scope: any): void {
    this.queue.lock(scope);
  }

  public unlock(scope: any): void {
    this.queue.unlock(scope);
  }

  public retryFailedItems(): void {
    this.queue.retryFailedItems();
  }

  public modifyQueueItems(
    modifyFunction: ModifyFunction<
      TSynchronizationEnvironmentTypes,
      TEntityTypesByEntityName
    >
  ): void {
    if (this.queue.modifyQueueItems(modifyFunction)) {
      this.saveQueueRateLimited();
    }
  }

  /**
   * If you just need info about which entities are in the queue, getItemsToSynchronizeInfos is probably a better fit.
   *
   * The data here is only exposed, so you can e.g. save all items somewhere else and then can logout without the data being gone completely.
   * This is necessary e.g. if some items are stuck in the upload queue and can't be synchronized because they are faulty.
   */
  public getItemsToSynchronizeCopies(): Array<
    EntityWithEntityName<
      TSynchronizationEnvironmentTypes,
      any,
      TEntityTypesByEntityName
    >
  > {
    return this.queue.getItemsToSave().map((item) => {
      return {
        entity: ObjectUtils.copyDeep(item.entity),
        entityName: item.entityName
      };
    });
  }

  private registerEntityHooks(): void {
    const repositories = this.entityRepositoryContainer.getAll();

    for (const repository of repositories) {
      const entityName = repository.getEntityInfo()
        .entityName as TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'];

      const hooks = {
        afterEntityCreated: this.createEntityHookHandler(
          entityName,
          this.handleEntityCreated.bind(this)
        ),
        afterEntityUpdated: this.createEntityHookHandler(
          entityName,
          this.handleEntityUpdated.bind(this)
        ),
        afterEntityDeleted: this.createEntityHookHandler(
          entityName,
          this.handleEntityDeleted.bind(this)
        )
      };

      this.disposableContainer.add(repository.registerHooks(hooks));
    }
  }

  private createEntityHookHandler<
    TEntityName extends
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']
  >(
    entityName: TEntityName,
    callback: (
      entityWithEntityName: EntityWithEntityName<
        TSynchronizationEnvironmentTypes,
        TEntityName,
        TEntityTypesByEntityName
      >
    ) => void
  ): (entity: TEntityTypesByEntityName[TEntityName]['entity']) => void {
    return (entity: TEntityTypesByEntityName[TEntityName]['entity']) => {
      if (!entity.shadowEntity) {
        callback({ entityName, entity });
      }
    };
  }

  private handleEntityCreated(
    entityWithEntityName: AnyEntityWithEntityName<
      TSynchronizationEnvironmentTypes,
      TEntityTypesByEntityName
    >
  ): void {
    this.addItem({
      type: QueueItemType.PUT,
      entityName: entityWithEntityName.entityName,
      entity: JSON.parse(JSON.stringify(entityWithEntityName.entity))
    });
  }

  private handleEntityUpdated(
    entityWithEntityName: AnyEntityWithEntityName<
      TSynchronizationEnvironmentTypes,
      TEntityTypesByEntityName
    >
  ): void {
    this.addItem({
      type: QueueItemType.PUT,
      entityName: entityWithEntityName.entityName,
      entity: JSON.parse(JSON.stringify(entityWithEntityName.entity))
    });
  }

  private handleEntityDeleted(
    entityWithEntityName: AnyEntityWithEntityName<
      TSynchronizationEnvironmentTypes,
      TEntityTypesByEntityName
    >
  ): void {
    this.addItem({
      type: QueueItemType.DELETE,
      entityName: entityWithEntityName.entityName,
      entity: JSON.parse(JSON.stringify(entityWithEntityName.entity))
    });
  }

  private addItem<
    TEntityName extends
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']
  >(
    item: EntitySynchronizationInfo<
      TSynchronizationEnvironmentTypes,
      TEntityName,
      TEntityTypesByEntityName
    >
  ): void {
    this.queue.addItem(item);
    this.saveQueueRateLimited();
    this.hooksContainer.callHooks(
      'itemsToSynchronizeInfosChanged',
      this.getItemsToSynchronizeInfos()
    );
  }

  private handleAfterItemUploaded<
    TEntityName extends
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']
  >(
    uploadFunctionReturnValue: AfterItemUploadedReturnValue<
      TSynchronizationEnvironmentTypes,
      TEntityName,
      TEntityTypesByEntityName
    >
  ): void {
    const localEntity = this.handleItemSynchronized(
      uploadFunctionReturnValue.result,
      uploadFunctionReturnValue.preSynchronizationEntity
    );

    this.hooksContainer.callHooks('afterEntitySynchronized', {
      entityName: uploadFunctionReturnValue.result.entityName,
      entity: localEntity,
      preSynchronizationEntity:
        uploadFunctionReturnValue.preSynchronizationEntity
    });

    if (
      uploadFunctionReturnValue.result.entity.id !==
      uploadFunctionReturnValue.preSynchronizationEntity.id
    ) {
      this.hooksContainer.callHooks('entityIdUpgraded', {
        entityName: uploadFunctionReturnValue.result.entityName,
        entity: localEntity,
        preSynchronizationEntity:
          uploadFunctionReturnValue.preSynchronizationEntity
      });
    }

    this.hooksContainer.callHooks(
      'itemsToSynchronizeInfosChanged',
      this.getItemsToSynchronizeInfos()
    );
  }

  private handleItemSynchronized<
    TEntityName extends
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']
  >(
    result: UploadResult<
      TSynchronizationEnvironmentTypes,
      TEntityName,
      TEntityTypesByEntityName
    >,
    preSynchronizationEntity: TEntityTypesByEntityName[TEntityName]['entity']
  ): TEntityTypesByEntityName[TEntityName]['entity'] {
    let localEntity = this.getLocalInstanceForResult(result);
    const repository = this.entityRepositoryContainer.getByEntityName(
      result.entityName
    );

    if (!result.entity.deleted) {
      if (localEntity) {
        localEntity = repository.updateLocally({
          ...result.entity,
          onlyLocal: false
        });
      }
    } else {
      this.removeEntityLocally(result.entityName, result.entity);

      // update the local entity so references to it have the correct values
      if (localEntity) {
        Object.assign(localEntity, result.entity);
      }
    }

    this.saveQueueRateLimited();
    return (
      localEntity || {
        ...preSynchronizationEntity,
        ...result.entity,
        onlyLocal: false
      }
    );
  }

  private removeEntityLocally<
    TEntityName extends
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']
  >(
    entityName: TEntityName,
    entity: TEntityTypesByEntityName[TEntityName]['entity']
  ): void {
    const repository =
      this.entityRepositoryContainer.getByEntityName(entityName);

    const localEntity = repository.getByIdIncludingInvisibleEntities(entity.id);
    if (localEntity) {
      repository.removeLocally(localEntity);
    }

    for (const originalId of entity.originalIds) {
      const originalEntity =
        repository.getByOriginalIdIncludingInvisibleEntities(originalId);
      if (originalEntity) {
        repository.removeLocally(originalEntity);
      }
    }
  }

  private getLocalInstanceForResult<
    TEntitityName extends
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']
  >(
    result: UploadResult<
      TSynchronizationEnvironmentTypes,
      TEntitityName,
      TEntityTypesByEntityName
    >
  ): TEntityTypesByEntityName[TEntitityName]['entity'] | null {
    const idsToCheck = [result.entity.id, ...result.entity.originalIds];
    const repository = this.entityRepositoryContainer.getByEntityName(
      result.entityName
    );

    for (const id of idsToCheck) {
      const entity = repository.getByIdIncludingInvisibleEntities(id);
      if (entity) {
        return entity;
      }
    }

    return null;
  }

  private async saveQueue(): Promise<void> {
    await StorageAdapter.setGeneralItem(
      EntitySynchronization.QUEUE_KEY,
      this.queue.getItemsToSave()
    );
  }

  private async loadQueue(): Promise<void> {
    const items:
      | Array<
          EntitySynchronizationInfo<
            TSynchronizationEnvironmentTypes,
            TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
            TEntityTypesByEntityName
          >
        >
      | undefined = await StorageAdapter.getGeneralItem(
      EntitySynchronization.QUEUE_KEY
    );

    if (items) {
      this.queue.addItems(items);
    } else {
      // @deprecated
      const toSynchronize =
        (await StorageAdapter.getGeneralItem('toSynchronize')) ?? [];
      const failedToSynchronize =
        (await StorageAdapter.getGeneralItem('failedSynchronizationItems')) ??
        [];
      const oldEntities: Array<any> = [
        ...toSynchronize.map((i: any) => i.entity),
        ...failedToSynchronize
      ];

      const convertedItems = oldEntities.map((oldEntity) => {
        const repository = this.entityRepositoryContainer.getByEntityName(
          oldEntity.entity_name
        );
        EntityInfoUtils.applyDefaultValues<
          TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'],
          any,
          any
        >(repository.getEntityInfo(), oldEntity);
        return {
          entityName: oldEntity.entity_name,
          entity: oldEntity,
          type: oldEntity.deleted ? QueueItemType.DELETE : QueueItemType.PUT
        };
      });

      this.queue.addItems(convertedItems);

      if (convertedItems.length) {
        await this.saveQueue();
        await StorageAdapter.removeGeneralItem('toSynchronize');
        await StorageAdapter.removeGeneralItem('failedSynchronizationItems');
      }
    }
  }

  private assertInitialized(): void {
    if (!this.initialized) {
      throw new Error(
        "can't use the EntitySynchronization before it is initialized"
      );
    }
  }
}

export type EntitySynchronizationHooks<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>
> = {
  afterEntitySynchronized?: (
    payload: AfterEntitySynchronizedPayload<
      TSynchronizationEnvironmentTypes,
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
      TEntityTypesByEntityName
    >
  ) => void;
  entityIdUpgraded?: (
    payload: EntityIdUpgradedPayload<
      TSynchronizationEnvironmentTypes,
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
      TEntityTypesByEntityName
    >
  ) => void;
  itemsToSynchronizeInfosChanged?: (
    itemsToSynchronizeInfos: Array<
      EntityToSynchronizeInfo<TSynchronizationEnvironmentTypes>
    >
  ) => void;
};

export type UploadFunction<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>
> = <
  TEntityName extends
    TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']
>(
  item: EntitySynchronizationInfo<
    TSynchronizationEnvironmentTypes,
    TEntityName,
    TEntityTypesByEntityName
  >
) => Promise<
  UploadResult<
    TSynchronizationEnvironmentTypes,
    TEntityName,
    TEntityTypesByEntityName
  >
>;

export type UploadResult<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityName extends
    TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>
> = EntityWithEntityName<
  TSynchronizationEnvironmentTypes,
  TEntityName,
  TEntityTypesByEntityName
>;

export type AfterEntitySynchronizedPayload<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityName extends
    TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>
> = EntityWithEntityName<
  TSynchronizationEnvironmentTypes,
  TEntityName,
  TEntityTypesByEntityName
> & {
  preSynchronizationEntity: TEntityTypesByEntityName[TEntityName]['entity'];
};

export type EntityIdUpgradedPayload<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityName extends
    TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>
> = EntityWithEntityName<
  TSynchronizationEnvironmentTypes,
  TEntityName,
  TEntityTypesByEntityName
> & {
  preSynchronizationEntity: TEntityTypesByEntityName[TEntityName]['entity'];
};

type AfterItemUploadedReturnValue<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityName extends
    TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>
> = {
  preSynchronizationEntity: TEntityTypesByEntityName[TEntityName]['entity'];
  result: UploadResult<
    TSynchronizationEnvironmentTypes,
    TEntityName,
    TEntityTypesByEntityName
  >;
};
